import React, { useContext } from 'react';
import { ApiContext } from '@api/api';
import { CompanyLocationCarriersApiInDTO, CompanyLocationsDetailOutDTO } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import LocationCarrierApiTag from '@components/ringil3/Features/Location/Tags/LocationCarrierApiTag.tsx';
import { isNotBlank } from '@utils/utils.tsx';
import SvgMicrochip from '@icons/microchip.svg?react';
import LocationFedexAccountsTag from '@components/ringil3/Features/Location/Tags/LocationFedexAccountsTag.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { FeaturesEnum } from '@api/ringil-api.v2';

interface ComponentProps {
  location: CompanyLocationsDetailOutDTO;
  onReload: () => void;
}

const CarriersApiSection: React.FC<ComponentProps> = ({ location, onReload }) => {
  const { tr } = useTranslationLgs();
  const { updateLocationCarriersApi } = useContext(ApiContext);
  const { hasFeature } = useContext(AuthenticatedUserContext);

  // A deleted location is read-only everywhere — Restore on the detail is the only action offered.
  const readOnly = location.isDeleted;

  const saveCarrierFields = (values: Record<string, string>) => {
    const normalized = Object.fromEntries(Object.entries(values).map(([key, value]) => [key, isNotBlank(value) ? value : null]));
    const body: CompanyLocationCarriersApiInDTO = {
      dachserBranch: location.dachserBranch,
      dachserCustomerNumber: location.dachserCustomerNumber,
      dachserToken: location.dachserToken,
      schenkerReferenceNumber: location.schenkerReferenceNumber,
      dhlAccountNumberExport: location.dhlAccountNumberExport,
      dhlAccountNumberImport: location.dhlAccountNumberImport,
      ...normalized,
    };
    updateLocationCarriersApi(location.id, body, () => onReload());
  };

  const saveFedexAccounts = (fedexAccountIds: string[]) => {
    const body: CompanyLocationCarriersApiInDTO = {
      dachserBranch: location.dachserBranch,
      dachserCustomerNumber: location.dachserCustomerNumber,
      dachserToken: location.dachserToken,
      schenkerReferenceNumber: location.schenkerReferenceNumber,
      dhlAccountNumberExport: location.dhlAccountNumberExport,
      dhlAccountNumberImport: location.dhlAccountNumberImport,
      fedexAccountIds,
    };
    updateLocationCarriersApi(location.id, body, () => onReload());
  };

  return (
    <SectionPanel icon={<SvgMicrochip className={'w-6 h-6'} />} iconColor={'var(--content-tertiary)'} heading={tr('LocalityDetail.carriersApi', 'Carriers API')}>
      <Ringil3BadgeRow>
        <LocationCarrierApiTag
          label={tr('LocalityDetail.carrierDachser', 'Dachser')}
          fields={[
            { key: 'dachserBranch', label: tr('LocalityDetail.dachserBranch', 'Branch'), value: location.dachserBranch },
            { key: 'dachserCustomerNumber', label: tr('LocalityDetail.dachserCustomerNumber', 'Customer number'), value: location.dachserCustomerNumber },
            { key: 'dachserToken', label: tr('LocalityDetail.dachserToken', 'Token'), value: location.dachserToken },
          ]}
          onChange={saveCarrierFields}
          readOnly={readOnly}
          dataTestId={'location-tag-dachser'}
        />
        <LocationCarrierApiTag
          label={tr('LocalityDetail.carrierSchenker', 'Schenker')}
          fields={[{ key: 'schenkerReferenceNumber', label: tr('LocalityDetail.schenkerReferenceNumber', 'Reference number'), value: location.schenkerReferenceNumber }]}
          onChange={saveCarrierFields}
          readOnly={readOnly}
          dataTestId={'location-tag-schenker'}
        />
        <LocationCarrierApiTag
          label={tr('LocalityDetail.carrierDhl', 'DHL')}
          fields={[
            { key: 'dhlAccountNumberExport', label: tr('LocalityDetail.dhlAccountNumberExport', 'Account number (export)'), value: location.dhlAccountNumberExport },
            { key: 'dhlAccountNumberImport', label: tr('LocalityDetail.dhlAccountNumberImport', 'Account number (import)'), value: location.dhlAccountNumberImport },
          ]}
          onChange={saveCarrierFields}
          readOnly={readOnly}
          dataTestId={'location-tag-dhl'}
        />
        {hasFeature([FeaturesEnum.FEDEX_API]) && (
          <LocationFedexAccountsTag value={location.fedexAccountIds ?? []} onChange={saveFedexAccounts} readOnly={readOnly} />
        )}
      </Ringil3BadgeRow>
    </SectionPanel>
  );
};

export default CarriersApiSection;
